import last from 'lodash/last'
import map from 'lodash/map'
import React, { FC, memo, useMemo } from 'react'

import { StarRating } from '@Components/StarRating'
import { ProductReview } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'
import { FlagsUnicode, LocaleToTrendhimDomain } from '@Shared/Statics/Flags/flagUnicode'

import styles from './ProductReviewItem.module.scss'

type IProductReviewItemProps = {
  currentLocale?: string
  review: ProductReview
}

export const ProductReviewItem: FC<IProductReviewItemProps> = memo(({ currentLocale, review }): React.JSX.Element => {
  const { t } = useTranslation()

  // We don't want to just inject the data uncritically, but we DO want linebreaks. This is a fast and easy
  // way of doing the linebreaks without actually allowing HTML to end up here.
  const processedTextArray = useMemo(() => (review.localizedText || '')
    .trim()
    .replace(/\n+/g, '\n')
    .split('\n'), [review.localizedText])

  return (
    <div className={styles.reviewWrapper}>
      <div className={styles.metaWrapper}>
        <div className={styles.headerInfo}>
          <p className={styles.header}>{review.localizedTitle}</p>
          <p>
            {`${t('products:BY_LABEL')} ${review.customerName} ${review.createdAt && new Date(review.createdAt).toLocaleDateString(currentLocale)}`}
          </p>
        </div>
        <StarRating
          activeColor="var(--black)"
          className={styles.star}
          inactiveBackgroundColor="transparent"
          inactiveForegroundColor="var(--black)"
          rating={review.rating || 0}
        />
      </div>
      <div className={styles.text}>{map(processedTextArray, line => <span key={line}>{line}</span>)}</div>
      {review.locale && review.locale !== currentLocale && (
        <div className={styles.translatedIndicator}>
          {t('products:TRANSLATED_FROM_LABEL')}
          <span className={styles.greyBanner}>
            {FlagsUnicode[last(review.locale.toLowerCase().split('-')) || '']}
            <p>{LocaleToTrendhimDomain[review.locale]}</p>
          </span>
        </div>
      )}
    </div>
  )
})
